import { toApiError } from '@/lib/http';

/**
 * The languages the product supports, from the server.
 *
 * Not a constant. `app_language` decides which languages exist, which are
 * translation targets, and what each needs to render — direction, font token
 * and script class. Adding a language is a row update there, and a hardcoded
 * list here would break that: English became a translation target by one SQL
 * statement, and a four-entry array in the editor would have needed a deploy
 * to catch up.
 *
 * One font per script and never a fallback across scripts, so `scriptClass`
 * comes from the server rather than being derived from the code.
 */

export interface Language {
  readonly code: string;
  readonly nameEn: string;
  readonly nameNative: string;
  readonly direction: 'ltr' | 'rtl';
  readonly scriptClass: string;
  readonly digitSystem: string;
  readonly target: boolean;
}

function parse(value: unknown): Language[] {
  if (!Array.isArray(value)) {
    throw new Error('the language catalogue is not an array');
  }
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const row = entry as Record<string, unknown>;
    if (typeof row.code !== 'string') {
      return [];
    }
    return [
      {
        code: row.code,
        nameEn: typeof row.nameEn === 'string' ? row.nameEn : row.code,
        nameNative: typeof row.nameNative === 'string' ? row.nameNative : row.code,
        // Defaulting to ltr rather than guessing from the code: a wrong
        // direction is a broken pane, and the server always sends one.
        direction: row.direction === 'rtl' ? 'rtl' : 'ltr',
        scriptClass:
          typeof row.scriptClass === 'string' && row.scriptClass !== ''
            ? row.scriptClass
            : 'script-latin',
        digitSystem: typeof row.digitSystem === 'string' ? row.digitSystem : 'latn',
        target: row.target === true,
      },
    ];
  });
}

export async function fetchLanguages(
  role: 'target' | 'all',
  signal: AbortSignal,
): Promise<Language[]> {
  const response = await fetch(
    role === 'target' ? '/api/public/languages?role=target' : '/api/public/languages',
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parse(await response.json());
}

/** `Malayalam · ML`, as the editor's picker labels them. */
export function languageLabel(language: Language): string {
  return `${language.nameEn} · ${language.code.toUpperCase()}`;
}
